<c-container fluid>
  <div class="d-flex justify-content-between align-items-center mb-4">
    <h2 class="mb-0">{{ 'wpcp.admin.title' | translate }}</h2>
    <button cButton variant="outline" color="secondary" (click)="recalcAll()">↻ {{ 'wpcp.admin.recalc_all' | translate }}</button>
  </div>

  <c-alert *ngIf="message() as m" [color]="m.type" [dismissible]="false">{{ m.text }}</c-alert>

  <!-- Search -->
  <c-card class="mb-4">
    <c-card-header><strong>{{ 'wpcp.admin.member' | translate }}</strong></c-card-header>
    <c-card-body>
      <div class="d-flex gap-2 mb-3">
        <input class="form-control" [(ngModel)]="queryModel" (keyup.enter)="search()"
               [placeholder]="'wpcp.admin.search_placeholder' | translate" />
        <button cButton color="primary" (click)="search()">🔍</button>
      </div>
      <div *ngIf="searching()" class="text-center py-2"><c-spinner size="sm" /></div>
      <table *ngIf="results().length" class="table table-hover table-sm">
        <tbody>
          <tr *ngFor="let u of results()" style="cursor:pointer" (click)="selectUser(u)">
            <td>{{ u.id }}</td>
            <!-- The backend resolves the name (User#display_name); the old
                 client-side fallback rendered "null null" when nothing was on file. -->
            <td>{{ u.full_name }}</td>
            <td>{{ u.email }}</td>
          </tr>
        </tbody>
      </table>

      <!-- Members that already have points (active DESC) -->
      <div *ngIf="!results().length && topAccounts().length" class="mt-2">
        <div class="text-medium-emphasis small mb-2">{{ topAccounts().length }} {{ 'wpcp.admin.with_points' | translate }}</div>
        <table class="table table-hover table-sm">
          <thead>
            <tr>
              <th>User</th><th>Email</th>
              <th class="text-end">{{ 'wpcp.active_points' | translate }}</th>
              <th class="text-end">{{ 'wpcp.auto_points' | translate }}</th>
              <th class="text-end">{{ 'wpcp.manual_points' | translate }}</th>
              <th class="text-end">{{ 'wpcp.deactivated_points' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let a of topAccounts()" style="cursor:pointer" (click)="selectAccount(a)">
              <td>{{ a.full_name }} <span class="text-medium-emphasis">#{{ a.user_id }}</span></td>
              <td class="small">{{ a.email }}</td>
              <td class="text-end fw-semibold">{{ a.active_points }}</td>
              <td class="text-end">{{ a.auto_points }}</td>
              <td class="text-end">{{ a.manual_points }}</td>
              <td class="text-end text-medium-emphasis">{{ a.deactivated_points }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </c-card-body>
  </c-card>

  <ng-container *ngIf="selected() as sel">
    <div *ngIf="loading()" class="text-center py-3"><c-spinner /></div>

    <ng-container *ngIf="summary() as s">
      <!-- Summary -->
      <c-card class="mb-4">
        <c-card-header>
          <strong>{{ 'wpcp.admin.summary' | translate }} — {{ s.personal.full_name }} (#{{ s.personal.id }})</strong>
        </c-card-header>
        <c-card-body>
          <c-row>
            <c-col [md]="3">
              <div class="fw-semibold mb-1">{{ 'wpcp.admin.personal' | translate }}</div>
              <div class="small">{{ s.personal.email }}</div>
              <div class="small">Sponsor: {{ s.personal.sponsor?.id || '—' }}</div>
              <div class="small">Status: {{ s.personal.membership_status }}</div>
              <!-- A converted entitlement carries no plan code (no rhythm was ever
                   bought), so show the coverage end — otherwise it reads as "nothing". -->
              @for (m of s.personal.active_memberships || []; track $index) {
                <div class="small">
                  {{ m.plan || 'Club (umgewandelt)' }}
                  <span class="text-medium-emphasis">bis {{ m.paid_through | date:'shortDate' }}</span>
                </div>
              }
              @if (!(s.personal.active_memberships || []).length) {
                <div class="small text-medium-emphasis">keine aktive Mitgliedschaft</div>
              }
            </c-col>
            <c-col [md]="3">
              <div class="fw-semibold mb-1">{{ 'wpcp.title' | translate }}</div>
              <div class="fs-5 fw-bold">{{ s.account.active_points }}</div>
              <div class="small">{{ 'wpcp.auto_points' | translate }}: {{ s.account.auto_points }} · {{ 'wpcp.manual_points' | translate }}: {{ s.account.manual_points }}</div>
              <!-- v4.0 has no admin unlock any more: 200 automatic + 50 special = 250
                   is the ceiling for everyone, so there is nothing to grant here. -->
              <div class="small">Max: {{ s.account.effective_max }}</div>
              <div class="mt-2 d-flex flex-column gap-1">
                <button cButton size="sm" color="secondary" variant="outline" (click)="recalcMember()">↻ {{ 'wpcp.admin.recalc' | translate }}</button>
              </div>
            </c-col>
            <c-col [md]="3">
              <div class="fw-semibold mb-1">{{ 'wpcp.admin.structure' | translate }}</div>
              <div class="small">Direkte gesamt: {{ s.community.direct?.total }}</div>
              <div class="small">{{ 'wpcp.admin.active_members' | translate }}: {{ s.community.direct?.active_members }}</div>
              <div class="small">Pack Builder: {{ s.community.direct?.pack_builders }}</div>
            </c-col>
            <c-col [md]="3">
              <div class="fw-semibold mb-1">{{ 'wpcp.admin.community' | translate }}</div>
              <div class="small">Gesamt: {{ s.community.total_members }}</div>
              <div class="small">{{ 'wpcp.admin.active_members' | translate }}: {{ s.community.active_members }}</div>
            </c-col>
          </c-row>
          <hr />
          <div class="fw-semibold mb-1">{{ 'wpcp.admin.revenue' | translate }}</div>
          <div class="small">Total lifetime: {{ s.revenue_volume?.total?.lifetime }} · 90d: {{ s.revenue_volume?.total?.last_90 }} · Direct lifetime: {{ s.revenue_volume?.direct?.lifetime }}</div>
        </c-card-body>
      </c-card>

      <!-- Award points -->
      <c-card class="mb-4">
        <c-card-header><strong>{{ 'wpcp.admin.award_title' | translate }}</strong></c-card-header>
        <c-card-body>
          <c-row class="g-2">
            <c-col [md]="3">
              <select class="form-select" [(ngModel)]="award.category_key">
                <option value="">{{ 'wpcp.admin.category' | translate }}</option>
                <option *ngFor="let c of manualCategories()" [value]="c.key">{{ c.name }}</option>
              </select>
            </c-col>
            <c-col [md]="3">
              <select class="form-select" [(ngModel)]="award.rule_code">
                <option value="">{{ 'wpcp.admin.rule' | translate }} (—)</option>
                <option *ngFor="let r of rulesForCategory(award.category_key)" [value]="r.code">{{ r.name }} ({{ r.min_points }}–{{ r.max_points }})</option>
              </select>
            </c-col>
            <c-col [md]="2">
              <input type="number" class="form-control" [(ngModel)]="award.points"
                     [min]="selectedAwardRule()?.min_points" [max]="selectedAwardRule()?.max_points"
                     [placeholder]="'wpcp.admin.points' | translate" />
              <div *ngIf="selectedAwardRule() as r" class="small text-medium-emphasis mt-1">{{ r.min_points }}–{{ r.max_points }}</div>
            </c-col>
            <c-col [md]="2"><input class="form-control" [(ngModel)]="award.reason" [placeholder]="'wpcp.admin.reason' | translate" /></c-col>
            <c-col [md]="4">
              <div class="d-flex gap-1 align-items-center">
                <input type="date" class="form-control form-control-sm" [(ngModel)]="award.valid_from" title="effective from" />
                <span class="small text-medium-emphasis">–</span>
                <input type="date" class="form-control form-control-sm" [(ngModel)]="award.valid_to" title="expires at" />
                <button cButton color="primary" (click)="submitAward()">{{ 'wpcp.admin.award' | translate }}</button>
              </div>
              <div class="small text-medium-emphasis mt-1">
                Leave the end date empty for an award that does not expire. A member may
                hold at most 50 active special points at a time.
              </div>
            </c-col>
          </c-row>
        </c-card-body>
      </c-card>

      <!-- History -->
      <c-card class="mb-4">
        <c-card-header><strong>{{ 'wpcp.history' | translate }}</strong></c-card-header>
        <c-card-body>
          <table class="table table-sm">
            <thead><tr><th>Cat</th><th>Rule</th><th>Pts</th><th>Src</th><th>State</th><th>Reason</th><th></th></tr></thead>
            <tbody>
              <tr *ngFor="let h of history()">
                <td>{{ h.category_key }}</td>
                <td>{{ h.rule_code }}</td>
                <td>{{ h.points }}</td>
                <td>{{ h.source }}</td>
                <td><c-badge [color]="h.state === 'active' ? 'success' : 'secondary'">{{ h.state }}</c-badge></td>
                <td class="small">{{ h.reason }}</td>
                <td><button cButton size="sm" variant="outline" color="secondary" (click)="deactivate(h)">{{ h.state === 'active' ? '✕' : '↺' }}</button></td>
              </tr>
            </tbody>
          </table>
        </c-card-body>
      </c-card>

      <!-- Manual membership booking -->
      <c-card class="mb-4">
        <c-card-header><strong>{{ 'wpcp.admin.booking_title' | translate }}</strong></c-card-header>
        <c-card-body>
          <c-row class="g-2">
            <c-col [md]="3">
              <select class="form-select" [(ngModel)]="booking.plan_code">
                <option value="">{{ 'wpcp.admin.product' | translate }}</option>
                <option *ngFor="let p of products()" [value]="p.code">{{ p.name }} ({{ p.price }} {{ p.currency }})</option>
              </select>
            </c-col>
            <c-col [md]="2"><input type="number" class="form-control" [(ngModel)]="booking.paid_amount" [placeholder]="'wpcp.admin.paid_amount' | translate" /></c-col>
            <c-col [md]="2"><input class="form-control" [(ngModel)]="booking.currency" [placeholder]="'wpcp.admin.currency' | translate" /></c-col>
            <c-col [md]="2"><input type="date" class="form-control" [(ngModel)]="booking.payment_date" /></c-col>
            <c-col [md]="3"><input class="form-control" [(ngModel)]="booking.reason" [placeholder]="'wpcp.admin.reason' | translate" /></c-col>
          </c-row>
          <c-row class="g-2 mt-1">
            <c-col [md]="3"><input type="number" class="form-control" [(ngModel)]="booking.referrer_id" [placeholder]="'wpcp.admin.referrer_id' | translate" /></c-col>
            <c-col [md]="5" class="d-flex align-items-center">
              <label class="form-check-label"><input type="checkbox" class="form-check-input me-2" [(ngModel)]="booking.no_referral" />{{ 'wpcp.admin.no_referral' | translate }}</label>
            </c-col>
            <c-col [md]="2"><button cButton color="primary" (click)="submitBooking()">{{ 'wpcp.admin.book' | translate }}</button></c-col>
          </c-row>
        </c-card-body>
      </c-card>
    </ng-container>
  </ng-container>
</c-container>
